import { Typography } from '@mui/material';
import React, { ChangeEvent, createRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from './file-upload-hook';

interface ImageUploaderProps {
  imageUrl: string | null;
  filePrefix: string; // the prefix to use for the uploaded image
  onChange: (url: string | null) => void;
  disabled?: boolean;
}

export const ImageUploader = ({
  imageUrl,
  filePrefix,
  onChange,
  disabled = false,
}: ImageUploaderProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  /**
   * NOTE - this uploads to s3 everytime the image is changed
   */
  const [uploadFile, fileUploading] = useUploadFile({
    onComplete: (imageUrl: string): void => {
      if (imageUrl) {
        onChange(imageUrl);
      } else {
        onChange(null);
        setError(t('unableToUploadImage'));
      }
    },
  });

  const handleImageChange = async (files: FileList | null) => {
    // if no files, clear the image
    if (!files) {
      onChange(null);
      return;
    }

    uploadFile(files, filePrefix, 'image');
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setInDropZone(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setInDropZone(true);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    await handleImageChange(files);
    setInDropZone(false);
  };

  const handleDragExit = () => {
    setInDropZone(false);
  };

  const [inDropZone, setInDropZone] = useState(false);
  const inputRef = createRef<HTMLInputElement>();
  const openFileFinder = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div>
      <div className="flex justify-between">
        {imageUrl && (
          <Typography
            variant="subtitle1"
            onClick={openFileFinder}
            className="hover:cursor-pointer text-blue-500 font-bold mb-1"
          >
            {t('replace')}
          </Typography>
        )}
      </div>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragExit={handleDragExit}
        onDragLeave={handleDragExit}
      >
        {fileUploading ? (
          <div>{t('loading')}</div>
        ) : imageUrl ? (
          <img src={imageUrl} className="max-h-20 rounded-md" />
        ) : (
          <div
            className={
              inDropZone
                ? 'bg-blue-300 min-h-10 border border-slate-200 rounded-md py-4 content-center'
                : 'bg-slate-50 min-h-10 border border-slate-200 rounded-md py-4 content-center'
            }
          >
            <div className="flex justify-center text-xs text-slate-600">
              <div
                onClick={openFileFinder}
                className="hover:cursor-pointer text-blue-500 font-semibold mr-1"
              >
                {t('clickToUpload')}
              </div>
              {t('orDragAndDropFile')}
            </div>
            <div className="text-xs text-center text-slate-600">
              {t('imageFormatsAndSize')}
            </div>
          </div>
        )}
        <input
          disabled={disabled}
          type="file"
          style={{ display: 'none' }}
          ref={inputRef}
          accept="image/*"
          onChange={async (event: ChangeEvent<HTMLInputElement>) =>
            await handleImageChange(event.target.files)
          }
        />
      </div>
      {error && <div className="text-red-500 text-xs pt-1">{error}</div>}
    </div>
  );
};
